import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useParams, useLocation } from "react-router-dom";
import { Header } from "./components/Header";
import { useNavigate } from "react-router-dom";
import { NavLink } from "react-router-dom";
import { ShimmerTable } from "react-shimmer-effects";
import toast, { Toaster } from "react-hot-toast";
import Navbar from "./components/Navbar";

const API_URL = "/api";

export default function DocumentCheckList() {
  const { id } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const [company, setCompany] = useState(null);
  const [formData, setFormData] = useState({});
  const [updatedCompany, setUpdatedCompany] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const response = await fetch(`${API_URL}/getComapnyProfile/${id}`);
        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        setCompany(data);
        console.log("data", data);
        setFormData(data?.DOCUMENTSCHECKLIST || {});
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanyDetails();
  }, [id]);

  useEffect(() => {
    if (company?.COMPANYINFO?.COMPANYNAME_ENGLISH) {
      document.title = company.COMPANYINFO.COMPANYNAME_ENGLISH;
    } else {
      document.title = "Company Log"; // fallback
    }
    return () => {
      document.title = "Company Log";
    };
  }, [company]);

  function formatDate(dateString) {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }
  function formatDateMM(dateString) {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  function getDaysDifference(targetDateString) {
    const today = new Date();
    const targetDate = new Date(targetDateString);
    today.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);
    const diffInMs = targetDate - today;
    const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));
    return diffInDays;
  }

  const handleChange = (e, section, field) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: e.target.value,
        CHECKINGDETAILS: prev[section]?.CHECKINGDETAILS || "",
      },
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(
        `${API_URL}/updateCompanyProfile?username=pritomrajkhowa`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            COMPANYID: id,
            update_data: { DOCUMENTSCHECKLIST: formData },
          }),
        }
      );

      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Update failed");
      toast.success("Document checklist updated successfully");

      const updatedResponse = await fetch(`${API_URL}/getComapnyProfile/${id}`);
      const updatedData = await updatedResponse.json();
      setFormData(updatedData?.DOCUMENTSCHECKLIST || {});
      setUpdatedCompany(updatedData);
      setIsEditing(false);
    } catch (err) {
      toast.error("Error updating company profile: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const renderStatusCell = (section) => {
    let value = formData?.[section]?.STATUS || "";
    if (section === "ENTEREXPIRYDATE") value = "";
    return isEditing ? (
      <select
        className="form-select"
        value={value}
        onChange={(e) => handleChange(e, section, "STATUS")}
      >
        <option value="">Select</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
        <option value="N/A">N/A</option>
      </select>
    ) : (
      <div
        style={{
          backgroundColor: value === "Yes" ? "#58a65c" : "#fdf3d0",
          color: value === "Yes" ? "#fff" : "#000",
          textAlign: "center",
        }}
      >
        {value || ""}
      </div>
    );
  };

  const criteriaLabels = {
    COMPANYNAMEISCORRECT: "Company name is correct",
    THEREGISTRATIONISCURRENT: "The registration is current",
    THERETURNCOVERSTHEMOSTRECENTPERIOD:
      "The return covers the most recent period",
    SHAREHOLDER_SMATCHORGANIZATIONCHART_IFPROVIDED:
      "Shareholder(s) match organization chart (if provided)",
    THECHARTISSIGNEDANDDATED: "The chart is signed and dated",
    STATEMENTSARESIGNEDBYAUDITOR: "Statements are signed by auditor",
  };

  const renderCheckCell = (section) => {
    // if (section === "SUBMITTEDBUSINESSREGISTRATION") return "";

    // Show the label for these fields instead of CHECKINGDETAILS
    if (
      section === "COMPANYNAMEISCORRECT" ||
      section === "THEREGISTRATIONISCURRENT" ||
      section === "THERETURNCOVERSTHEMOSTRECENTPERIOD" ||
      section === "SHAREHOLDER_SMATCHORGANIZATIONCHART_IFPROVIDED" ||
      section === "THECHARTISSIGNEDANDDATED" ||
      section === "STATEMENTSARESIGNEDBYAUDITOR"
    ) {
      return criteriaLabels[section] || "";
    }

    if (section === "ENTEREXPIRYDATE") {
      const rawDate = formData?.ENTEREXPIRYDATE?.CHECKINGDETAILS || "";

      // If editing -> show a date picker. Otherwise show formatted date.
      return isEditing ? (
        <input
          type="date"
          className="form-control"
          // convert any ISO-like string to yyyy-mm-dd for input[type=date]
          value={rawDate ? new Date(rawDate).toISOString().split("T")[0] : ""}
          onChange={(e) => {
            const picked = e.target.value; // format: 'YYYY-MM-DD'
            // store as ISO string (keeps consistency with other date fields)
            const iso = picked ? new Date(picked).toISOString() : null;
            handleInputChange("ENTEREXPIRYDATE", "CHECKINGDETAILS", iso);
          }}
        />
      ) : (
        (rawDate && formatDate(rawDate)) || ""
      );
    }

    return formData?.[section]?.CHECKINGDETAILS || "";
  };

  const renderRedFlagCell = (section) => {
    const status = formData?.[section]?.STATUS;
    const isMandatory = formData?.[section]?.ISMENDATORY === true;

    if (isMandatory && status === "No") {
      return "Red Flag"; // show red flag
    }

    if (isMandatory && status === "Yes") {
      return ""; // no red flag text, but grey background
    }

    return ""; // default
  };

  const documentFields = [
    {
      label: "Submitted Business registration",
      key: "SUBMITTEDBUSINESSREGISTRATION",
    },
    {
      label: "Company name is correct",
      key: "COMPANYNAMEISCORRECT",
    },
    {
      label: "The registration is current",
      key: "THEREGISTRATIONISCURRENT",
    },
    { label: "Enter expiry date", key: "ENTEREXPIRYDATE", isDateDiff: true },
    {
      label: "Submitted Annual Return (NAR1)",
      key: "SUBMITTEDANNUALRETURN_NAR1",
    },
    {
      label: "Company name is correct",
      key: "COMPANYNAMEISCORRECT",
    },
    {
      label: "The return covers the most recent period",
      key: "THERETURNCOVERSTHEMOSTRECENTPERIOD",
    },
    {
      label: "Shareholder(s) match organization chart (if provided)",
      key: "SHAREHOLDER_SMATCHORGANIZATIONCHART_IFPROVIDED",
    },
    {
      label: "Submitted Certificate of Incorporation",
      key: "SUBMITTEDCERTIFICATEOFINCORPORATION",
    },
    {
      label: "Submitted Memorandum & Articles",
      key: "SUBMITTEDMEMORANDUMANDARTICLES",
    },
    {
      label: "Submitted Organization Chart (if applicable)",
      key: "SUBMITTEDORGANIZATIONCHART_IFAPPLICABLE",
    },
    {
      label: "The chart is signed and dated",
      key: "THECHARTISSIGNEDANDDATED",
    },
    {
      label: "Submitted Financial statements (Audited) for the last 3 years",
      key: "SUBMITTEDFINANCIALSTATEMENTS_AUDITEDFORTHELAST3YEARS",
    },
    {
      label: "Statements are signed by auditor",
      key: "STATEMENTSARESIGNEDBYAUDITOR",
    },
    {
      label:
        "Submitted Financial statements (Audited) for at least 1 of the last 2 FYs",
      key: "SUBMITTEDFINANCIALSTATEMENTS_AUDITEDFORATLEAST1OFTHELAST2FYS",
    },
    {
      label:
        "Submitted Unaudited Management Accounts for the most recent period (that are error-free)",
      key: "SUBMITTEDUNAUDITEDMANAGEMENTACCOUNTSFORTHEMOSTRECENTPERIOD_THATAREERROR_FREE",
    },
    {
      label:
        "Submitted Unaudited Management Accounts for the most recent period",
      key: "SUBMITTEDUNAUDITEDMANAGEMENTACCOUNTSFORTHEMOSTRECENTPERIOD",
    },
    {
      label: "Submitted Bank Account Information",
      key: "SUBMITTEDBANKACCOUNTINFORMATION",
    },
    {
      label:
        "Submitted all Bank Account statements for the last 3 months (required for loans)",
      key: "SUBMITTEDALLBANKACCOUNTSTATEMENTSFORTHELAST3MONTHS_REQUIREDFORLOANS",
    },
    {
      label:
        "Submitted Evidence of Signatory authorisation (in case signatory is not a Director of the company)",
      key: "SUBMITTEDEVIDENCEOFSIGNATORYAUTHORISATION_INCASESIGNATORYISNOTADIRECTOROFTHECOMPANY",
    },
    {
      label:
        "Submitted list of ongoing trade disputes and litigation with an explanation for each (if applicable)",
      key: "SUBMITTEDLISTOFONGOINGTRADEDISPUTESANDLITIGATIONWITHANEXPLANATIONFOREACH_IFAPPLICABLE",
    },
  ];

  const filteredDocumentFields = documentFields.filter(
    (field) =>
      formData?.[field.key] !== undefined && formData?.[field.key] !== null
  );

  // existing documentFields array...
  const documentKeys = filteredDocumentFields.map((field) => field.key);
  // console.log("documentKeys", documentKeys);

  // get additional fields from formData that are not in documentFields
  const extraFields = Object.keys(formData || {}).filter(
    (key) => !documentKeys.includes(key)
  );
  // console.log("extraFields", extraFields);

  // merge documentFields with extra fields
  const allFields = [
    ...filteredDocumentFields,
    ...extraFields.map((key) => ({ label: key, key })),
  ];

  const hiddenCriteriaKeys = [
    "THEREGISTRATIONISCURRENT",
    "COMPANYNAMEISCORRECT",
    "THERETURNCOVERSTHEMOSTRECENTPERIOD",
    "SHAREHOLDER_SMATCHORGANIZATIONCHART_IFPROVIDED",
    "THECHARTISSIGNEDANDDATED",
    "STATEMENTSARESIGNEDBYAUDITOR",
  ];

  const handleInputChange = (sectionKey, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [field]: value,
      },
    }));
  };

  return (
    <div className="app-container">
      <Toaster />
      <div className="container-fluid">
        <main className="content">
          <div className="row">
            <Navbar />

            <div className="col-md-10" style={{ marginTop: "30px" }}>
              <NavLink to={`/Dashboard`} className="btn btn-dark ms-2">
                Back to Company Log
              </NavLink>

              <br />
              <br />

              <NavLink
                to={`/details/${id}`}
                className="btn btn-outline-secondary ms-2"
              >
                Company Information
              </NavLink>

              <NavLink
                to={`/DocumentCheckList/${id}`}
                state={{ company: updatedCompany || company }}
                className="btn btn-dark ms-2"
              >
                Document Checklist
              </NavLink>

              <NavLink
                to={`/Searches/${id}`}
                state={{ company: updatedCompany || company }}
                className="btn btn-outline-secondary ms-2"
              >
                Searches
              </NavLink>

              <NavLink
                to={`/FinancialAnalysis/${id}`}
                state={{ company: updatedCompany || company }}
                className="btn btn-outline-secondary ms-2"
              >
                Financial Analysis
              </NavLink>

              <NavLink
                to={`/CompanyScore/${id}`}
                state={{ company: updatedCompany || company }}
                className="btn btn-outline-secondary ms-2"
              >
                Company Score
              </NavLink>

              <NavLink
                to={`/ScoringCriteria/${id}`}
                className="btn btn-outline-secondary ms-2"
              >
                Scoring Criteria
              </NavLink>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 style={{ marginTop: "10px" }}>
                  Document Checklist
                  <span
                    style={{
                      paddingLeft: "20px",
                      color: "red",
                      fontSize: "20px",
                      fontWeight: "bold",
                    }}
                  >
                    {company?.VersionString}
                  </span>
                </h3>
                <div>
                  {isEditing && (
                    <button
                      className="btn btn-secondary me-2"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={() =>
                      isEditing ? handleSubmit() : setIsEditing(true)
                    }
                    disabled={isSubmitting}
                  >
                    {isEditing
                      ? isSubmitting
                        ? "Submitting..."
                        : "Submit"
                      : "Edit"}
                  </button>
                </div>
              </div>

              {loading ? (
                <ShimmerTable row={5} col={5} />
              ) : (
                <>
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <td>Company Name:</td>
                        <td>{company?.COMPANYINFO?.COMPANYNAME_ENGLISH}</td>
                      </tr>
                      <tr>
                        <td>BR Number:</td>
                        <td>
                          {company?.COMPANYINFO?.BUSINESSREGISTRATIONNUMBER}
                        </td>
                      </tr>
                      <tr>
                        <td>Date:</td>
                        <td>
                          {company?.ReviewByData?.DATE &&
                          company?.ReviewByData?.DATE !== null
                            ? formatDateMM(company?.ReviewByData?.DATE)
                            : ""}
                        </td>
                      </tr>
                      <tr>
                        <td>Date Reviewed:</td>
                        <td>
                          {company?.ReviewByData?.DATE_REVIEW &&
                          company?.ReviewByData?.DATE_REVIEW !== null
                            ? formatDateMM(company?.ReviewByData?.DATE_REVIEW)
                            : ""}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <table className="table table-bordered">
                    <thead>
                      <tr
                        style={{
                          backgroundColor: "#173660",
                          color: "#fff",
                          textAlign: "center",
                        }}
                      >
                        <th>Criteria</th>
                        <th>Checking Details</th>
                        <th>Status/Select from Dropdown/</th>
                        <th>Comments</th>
                        <th>Red Flag</th>
                        <th>Reviewer Check</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allFields.map(({ label, key, isDateDiff }) => (
                        <tr key={key}>
                          {/* Criteria column */}
                          <td
                            style={{
                              textAlign:
                                label === "Enter expiry date"
                                  ? "right"
                                  : "left",
                            }}
                          >
                            {hiddenCriteriaKeys.includes(key)
                              ? ""
                              : label || key}
                          </td>

                          {/* Checking Details column */}
                          <td>{renderCheckCell(key)}</td>

                          {/* Status column */}
                          <td>{renderStatusCell(key)}</td>

                          {/* Date difference column */}
                          <td style={{ textAlign: "center" }}>
                            {(() => {
                              const isMandatory =
                                formData?.[key]?.ISMENDATORY === true;
                              const rawValue = formData?.[key]?.COMMENTS || "";
                              const diff =
                                isDateDiff && formData?.[key]?.CHECKINGDETAILS
                                  ? getDaysDifference(
                                      formData?.[key]?.CHECKINGDETAILS
                                    )
                                  : null;

                              // Check if COMMENTS starts with "Days to Renewal"
                              const isDaysToRenewalComment =
                                rawValue.startsWith("Days to renewal");

                              return (
                                <div>
                                  {/* Show COMMENTS only if it's not Days to Renewal */}
                                  {!isDaysToRenewalComment && rawValue && (
                                    <span
                                      style={{
                                        display: "block",
                                        marginBottom: diff ? "4px" : "0",
                                        backgroundColor: "#f8f8f8",
                                        padding: "3px 6px",
                                        borderRadius: "4px",
                                      }}
                                    >
                                      {rawValue}
                                    </span>
                                  )}

                                  {/* Always show calculated Days to Renewal if available */}
                                  {diff !== null && (
                                    <span
                                      style={{
                                        color: diff < 0 ? "red" : "green",
                                        backgroundColor: isMandatory
                                          ? "#d3d3d3"
                                          : "#fff",
                                        padding: "3px 6px",
                                        borderRadius: "4px",
                                        display: "inline-block",
                                      }}
                                    >
                                      Days to Renewal: {diff}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>

                          {/* Red Flag column */}
                          <td
                            style={{
                              backgroundColor:
                                formData?.[key]?.ISMENDATORY === true
                                  ? formData?.[key]?.STATUS === "No"
                                    ? "red" // No + Mandatory
                                    : "grey" // Yes + Mandatory
                                  : "white", // Non-mandatory
                              color:
                                formData?.[key]?.ISMENDATORY === true &&
                                formData?.[key]?.STATUS === "No"
                                  ? "white"
                                  : "black",
                              textAlign: "center",
                            }}
                          >
                            {renderRedFlagCell(key)}
                          </td>

                          {/* Reviewer Check column */}
                          <td style={{ textAlign: "center" }}>
                            {isEditing ? (
                              <select
                                value={formData?.[key]?.REVIEWERCHECK || "N/A"}
                                onChange={(e) =>
                                  handleInputChange(
                                    key,
                                    "REVIEWERCHECK",
                                    e.target.value
                                  )
                                }
                                style={{ padding: "4px", borderRadius: "4px" }}
                              >
                                <option value="N/A">N/A</option>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                              </select>
                            ) : (
                              formData?.[key]?.REVIEWERCHECK || "N/A"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
